import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MapPin,
  ArrowLeft,
  Building2,
  Users,
  Package,
  Truck,
  CheckCircle,
  FileText,
  Wrench,
  Receipt,
  Plus,
  Trash2,
  Save,
  AlertCircle,
  Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const workflowStages = [
  { key: 'purchase_order', label: 'Purchase Order', labelAr: 'أمر الشراء', icon: Package, color: 'bg-blue-500' },
  { key: 'material_issue', label: 'Material Issue', labelAr: 'إصدار المواد', icon: Truck, color: 'bg-indigo-500' },
  { key: 'material_receive', label: 'Material Receive', labelAr: 'استلام المواد', icon: Package, color: 'bg-purple-500' },
  { key: 'team_assigned', label: 'Team Assignment', labelAr: 'تعيين الفريق', icon: Users, color: 'bg-pink-500' },
  { key: 'implementation', label: 'Implementation', labelAr: 'التنفيذ', icon: Wrench, color: 'bg-orange-500' },
  { key: 'site_complete', label: 'Site Complete', labelAr: 'اكتمال الموقع', icon: CheckCircle, color: 'bg-green-500' },
  { key: 'handover', label: 'Handover', labelAr: 'التسليم', icon: FileText, color: 'bg-teal-500' },
  { key: 'pat_document', label: 'PAT Document', labelAr: 'وثيقة PAT', icon: FileText, color: 'bg-cyan-500' },
  { key: 'troubleshoot', label: 'Troubleshoot', labelAr: 'استكشاف الأخطاء', icon: AlertCircle, color: 'bg-red-500' },
  { key: 'invoices', label: 'Invoice', labelAr: 'الفاتورة', icon: Receipt, color: 'bg-emerald-500' },
];

const SiteDetail = () => {
  const { t, i18n } = useTranslation();
  const { siteId } = useParams();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const { currencySymbol } = useSettings();

  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [activeStage, setActiveStage] = useState('purchase_order');
  const [stageData, setStageData] = useState({});
  
  // Dialogs
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [teamForm, setTeamForm] = useState({});
  const [invoiceForm, setInvoiceForm] = useState({});

  useEffect(() => {
    fetchSite();
    fetchEmployees();
  }, [siteId]);

  const fetchSite = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/jobs/sites/${siteId}`);
      setSite(res.data);
      setStageData(res.data.workflow || {});
    } catch (err) {
      toast.error('Failed to load site');
      navigate('/projects/sites');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hr/employees`);
      setEmployees(res.data.filter(e => e.status === 'active'));
    } catch (err) {
      console.error('Failed to fetch employees');
    }
  };

  const handleSaveWorkflow = async () => {
    try {
      await axios.put(`${API_URL}/api/jobs/sites/${siteId}/workflow`, stageData);
      toast.success(isRTL ? 'تم حفظ التحديثات' : 'Workflow updated successfully');
      fetchSite();
    } catch (err) {
      toast.error(isRTL ? 'فشل في الحفظ' : 'Failed to save');
    }
  };

  const handleAddTeam = async () => {
    try {
      const employee = employees.find(e => e.id === teamForm.employee_id);
      const payload = {
        ...teamForm,
        employee_name: employee?.name || ''
      };
      await axios.post(`${API_URL}/api/jobs/sites/${siteId}/workflow/team`, payload);
      toast.success(isRTL ? 'تم إضافة عضو الفريق' : 'Team member added');
      setShowTeamDialog(false);
      setTeamForm({});
      fetchSite();
    } catch (err) {
      toast.error(isRTL ? 'فشل في الإضافة' : 'Failed to add team member');
    }
  };

  const handleRemoveTeam = async (memberId) => {
    if (window.confirm(isRTL ? 'هل أنت متأكد؟' : 'Remove this team member?')) {
      try {
        await axios.delete(`${API_URL}/api/jobs/sites/${siteId}/workflow/team/${memberId}`);
        toast.success(isRTL ? 'تم الحذف' : 'Team member removed');
        fetchSite();
      } catch (err) {
        toast.error(isRTL ? 'فشل في الحذف' : 'Failed to remove');
      }
    }
  };

  const handleAddInvoice = async () => {
    try {
      await axios.post(`${API_URL}/api/jobs/sites/${siteId}/workflow/invoice`, invoiceForm);
      toast.success(isRTL ? 'تم إضافة الفاتورة' : 'Invoice added');
      setShowInvoiceDialog(false);
      setInvoiceForm({});
      fetchSite();
    } catch (err) {
      toast.error(isRTL ? 'فشل في الإضافة' : 'Failed to add invoice');
    }
  };

  const updateStageField = (stage, field, value) => {
    setStageData(prev => ({
      ...prev,
      [stage]: {
        ...(prev[stage] || {}),
        [field]: value
      }
    }));
  };

  const getStageStatus = (stageKey) => {
    const data = stageData[stageKey];
    if (!data) return 'not_started';
    
    if (stageKey === 'team_assigned') {
      return (stageData.team_assigned?.length > 0) ? 'completed' : 'not_started';
    }
    if (stageKey === 'invoices') {
      return (stageData.invoices?.length > 0) ? 'completed' : 'not_started';
    }
    if (stageKey === 'troubleshoot') {
      return data.status || 'none';
    }
    return data.status || 'not_started';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      not_started: { label: isRTL ? 'لم يبدأ' : 'Not Started', class: 'bg-gray-100 text-gray-700' },
      in_progress: { label: isRTL ? 'جاري' : 'In Progress', class: 'bg-amber-100 text-amber-700' },
      completed: { label: isRTL ? 'مكتمل' : 'Completed', class: 'bg-green-100 text-green-700' },
      issued: { label: isRTL ? 'تم الإصدار' : 'Issued', class: 'bg-blue-100 text-blue-700' },
      received: { label: isRTL ? 'تم الاستلام' : 'Received', class: 'bg-green-100 text-green-700' },
      handed_over: { label: isRTL ? 'تم التسليم' : 'Handed Over', class: 'bg-teal-100 text-teal-700' },
      signed: { label: isRTL ? 'موقع' : 'Signed', class: 'bg-emerald-100 text-emerald-700' },
      none: { label: isRTL ? 'لا يوجد' : 'No Issues', class: 'bg-gray-100 text-gray-700' },
      open: { label: isRTL ? 'مفتوح' : 'Open', class: 'bg-red-100 text-red-700' },
      resolved: { label: isRTL ? 'تم الحل' : 'Resolved', class: 'bg-green-100 text-green-700' },
      pending: { label: isRTL ? 'قيد الانتظار' : 'Pending', class: 'bg-amber-100 text-amber-700' },
      ordered: { label: isRTL ? 'تم الطلب' : 'Ordered', class: 'bg-blue-100 text-blue-700' },
      sent: { label: isRTL ? 'مرسلة' : 'Sent', class: 'bg-blue-100 text-blue-700' },
      paid: { label: isRTL ? 'مدفوعة' : 'Paid', class: 'bg-green-100 text-green-700' },
    };
    const config = statusConfig[status] || statusConfig.not_started;
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  if (!site) {
    return <div className="text-center py-8">{isRTL ? 'الموقع غير موجود' : 'Site not found'}</div>;
  }

  const workflow = site.workflow || {};

  return (
    <div className={`space-y-6 ${isRTL ? 'rtl' : ''}`}>
      {/* Header */}
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className={`h-5 w-5 ${isRTL ? 'rotate-180' : ''}`} />
          </Button>
          <div>
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <MapPin className="h-6 w-6 text-primary" />
              <h1 className="text-2xl font-bold">{site.name}</h1>
              <Badge variant="outline">{site.site_number}</Badge>
            </div>
            <div className={`flex items-center gap-4 mt-1 text-sm text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
              {site.project_name && (
                <span className={`flex items-center gap-1 cursor-pointer hover:text-primary ${isRTL ? 'flex-row-reverse' : ''}`} onClick={() => navigate(`/projects/${site.project_id}`)}>
                  <Briefcase className="h-4 w-4" /> {site.project_name}
                </span>
              )}
              <span className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}><Building2 className="h-4 w-4" /> {site.client_name}</span>
              {site.location?.city && <span className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}><MapPin className="h-4 w-4" /> {site.location.city}</span>}
            </div>
          </div>
        </div>
        <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className={`h-4 w-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
            {isRTL ? 'رجوع' : 'Back'}
          </Button>
        </div>
      </div>

      {/* Progress Overview */}
      <Card>
        <CardHeader>
          <CardTitle>{isRTL ? 'تقدم الموقع' : 'Site Progress'}</CardTitle>
          <CardDescription>{isRTL ? 'النسبة الإجمالية للإنجاز' : 'Overall completion percentage'}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className={`flex justify-between text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span>{isRTL ? 'التقدم' : 'Progress'}</span>
              <span className="font-semibold">{workflow.overall_progress || 0}%</span>
            </div>
            <Progress value={workflow.overall_progress || 0} className="h-3" />
          </div>
          {/* Stage Progress Icons */}
          <div className={`flex justify-between mt-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {workflowStages.slice(0, 8).map((stage, index) => {
              const status = getStageStatus(stage.key);
              const isCompleted = ['completed', 'issued', 'received', 'handed_over', 'signed', 'resolved'].includes(status);
              const Icon = stage.icon;
              return (
                <div key={stage.key} className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isCompleted ? stage.color : 'bg-gray-200'}`}>
                    <Icon className={`h-5 w-5 ${isCompleted ? 'text-white' : 'text-gray-500'}`} />
                  </div>
                  <span className="text-xs mt-1 text-center max-w-[60px]">{isRTL ? stage.labelAr : stage.label}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Workflow Tabs */}
      <Tabs value={activeStage} onValueChange={setActiveStage}>
        <TabsList className="flex flex-wrap h-auto gap-1">
          {workflowStages.map(stage => (
            <TabsTrigger key={stage.key} value={stage.key} className="gap-1 text-xs">
              <stage.icon className="h-3 w-3" />
              {isRTL ? stage.labelAr : stage.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Purchase Order Stage */}
        <TabsContent value="purchase_order" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Package className="h-5 w-5" /> {isRTL ? 'أمر الشراء' : 'Purchase Order'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isRTL ? 'رقم أمر الشراء' : 'PO Number'}</Label>
                  <Input 
                    value={stageData.purchase_order?.po_number || ''} 
                    onChange={e => updateStageField('purchase_order', 'po_number', e.target.value)}
                    placeholder="PO-001"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'تاريخ أمر الشراء' : 'PO Date'}</Label>
                  <Input 
                    type="date"
                    value={stageData.purchase_order?.po_date || ''} 
                    onChange={e => updateStageField('purchase_order', 'po_date', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'المبلغ' : 'Amount'}</Label>
                  <Input 
                    type="number"
                    value={stageData.purchase_order?.amount || ''} 
                    onChange={e => updateStageField('purchase_order', 'amount', parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'الحالة' : 'Status'}</Label>
                  <Select 
                    value={stageData.purchase_order?.status || 'pending'} 
                    onValueChange={v => updateStageField('purchase_order', 'status', v)}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">{isRTL ? 'قيد الانتظار' : 'Pending'}</SelectItem>
                      <SelectItem value="ordered">{isRTL ? 'تم الطلب' : 'Ordered'}</SelectItem>
                      <SelectItem value="received">{isRTL ? 'تم الاستلام' : 'Received'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'ملاحظات' : 'Notes'}</Label>
                <Textarea 
                  value={stageData.purchase_order?.notes || ''} 
                  onChange={e => updateStageField('purchase_order', 'notes', e.target.value)}
                  rows={2}
                />
              </div>
              <div className={`flex justify-end pt-4 border-t ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Button onClick={handleSaveWorkflow}>
                  <Save className="h-4 w-4 mr-2" />
                  {isRTL ? 'حفظ التغييرات' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Material Issue Stage */}
        <TabsContent value="material_issue" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Truck className="h-5 w-5" /> {isRTL ? 'إصدار المواد' : 'Material Issue'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isRTL ? 'تاريخ الإصدار' : 'Issue Date'}</Label>
                  <Input 
                    type="date"
                    value={stageData.material_issue?.issue_date || ''} 
                    onChange={e => updateStageField('material_issue', 'issue_date', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'صدر بواسطة' : 'Issued By'}</Label>
                  <Input 
                    value={stageData.material_issue?.issued_by || ''} 
                    onChange={e => updateStageField('material_issue', 'issued_by', e.target.value)}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>{isRTL ? 'العناصر' : 'Items'}</Label>
                  <Textarea 
                    value={stageData.material_issue?.items || ''} 
                    onChange={e => updateStageField('material_issue', 'items', e.target.value)}
                    placeholder={isRTL ? 'وصف المواد المصدرة...' : 'Description of issued materials...'}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'الحالة' : 'Status'}</Label>
                  <Select 
                    value={stageData.material_issue?.status || 'not_started'} 
                    onValueChange={v => updateStageField('material_issue', 'status', v)}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">{isRTL ? 'لم يبدأ' : 'Not Started'}</SelectItem>
                      <SelectItem value="issued">{isRTL ? 'تم الإصدار' : 'Issued'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'ملاحظات' : 'Notes'}</Label>
                <Textarea 
                  value={stageData.material_issue?.notes || ''} 
                  onChange={e => updateStageField('material_issue', 'notes', e.target.value)}
                  rows={2}
                />
              </div>
              <div className={`flex justify-end pt-4 border-t ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Button onClick={handleSaveWorkflow}>
                  <Save className="h-4 w-4 mr-2" />
                  {isRTL ? 'حفظ التغييرات' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Material Receive Stage */}
        <TabsContent value="material_receive" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Package className="h-5 w-5" /> {isRTL ? 'استلام المواد' : 'Material Receive'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isRTL ? 'تاريخ الاستلام' : 'Receive Date'}</Label>
                  <Input 
                    type="date"
                    value={stageData.material_receive?.receive_date || ''} 
                    onChange={e => updateStageField('material_receive', 'receive_date', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'استلم بواسطة' : 'Received By'}</Label>
                  <Input 
                    value={stageData.material_receive?.received_by || ''} 
                    onChange={e => updateStageField('material_receive', 'received_by', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'الحالة' : 'Condition'}</Label>
                  <Select 
                    value={stageData.material_receive?.condition || 'good'} 
                    onValueChange={v => updateStageField('material_receive', 'condition', v)}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="good">{isRTL ? 'جيدة' : 'Good'}</SelectItem>
                      <SelectItem value="damaged">{isRTL ? 'تالفة' : 'Damaged'}</SelectItem>
                      <SelectItem value="partial">{isRTL ? 'جزئية' : 'Partial'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'الحالة' : 'Status'}</Label>
                  <Select 
                    value={stageData.material_receive?.status || 'not_started'} 
                    onValueChange={v => updateStageField('material_receive', 'status', v)}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">{isRTL ? 'لم يبدأ' : 'Not Started'}</SelectItem>
                      <SelectItem value="received">{isRTL ? 'تم الاستلام' : 'Received'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'ملاحظات' : 'Notes'}</Label>
                <Textarea 
                  value={stageData.material_receive?.notes || ''} 
                  onChange={e => updateStageField('material_receive', 'notes', e.target.value)}
                  rows={2}
                />
              </div>
              <div className={`flex justify-end pt-4 border-t ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Button onClick={handleSaveWorkflow}>
                  <Save className="h-4 w-4 mr-2" />
                  {isRTL ? 'حفظ التغييرات' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Assignment Stage */}
        <TabsContent value="team_assigned" className="mt-4">
          <Card>
            <CardHeader className={`flex flex-row items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Users className="h-5 w-5" /> {isRTL ? 'تعيين الفريق' : 'Team Assignment'}
              </CardTitle>
              <Button size="sm" onClick={() => { setTeamForm({}); setShowTeamDialog(true); }}>
                <Plus className="h-4 w-4 mr-1" /> {isRTL ? 'إضافة عضو' : 'Add Member'}
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(stageData.team_assigned || []).map(member => (
                  <div key={member.id} className={`flex items-center justify-between p-3 bg-muted/50 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{member.employee_name}</p>
                        <p className="text-sm text-muted-foreground">{member.role || '-'}</p>
                      </div>
                    </div>
                    <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span className="text-sm text-muted-foreground">{member.assigned_date?.slice(0, 10)}</span>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleRemoveTeam(member.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {(!stageData.team_assigned || stageData.team_assigned.length === 0) && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>{isRTL ? 'لم يتم تعيين فريق بعد' : 'No team members assigned yet'}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Implementation Stage */}
        <TabsContent value="implementation" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Wrench className="h-5 w-5" /> {isRTL ? 'التنفيذ' : 'Implementation'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isRTL ? 'تاريخ البدء' : 'Start Date'}</Label>
                  <Input 
                    type="date"
                    value={stageData.implementation?.start_date || ''} 
                    onChange={e => updateStageField('implementation', 'start_date', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'تاريخ الانتهاء' : 'End Date'}</Label>
                  <Input 
                    type="date"
                    value={stageData.implementation?.end_date || ''} 
                    onChange={e => updateStageField('implementation', 'end_date', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'نسبة الإنجاز' : 'Progress'} ({stageData.implementation?.progress || 0}%)</Label>
                  <Input 
                    type="range"
                    min="0"
                    max="100"
                    value={stageData.implementation?.progress || 0} 
                    onChange={e => updateStageField('implementation', 'progress', parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'الحالة' : 'Status'}</Label>
                  <Select 
                    value={stageData.implementation?.status || 'not_started'} 
                    onValueChange={v => updateStageField('implementation', 'status', v)}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">{isRTL ? 'لم يبدأ' : 'Not Started'}</SelectItem>
                      <SelectItem value="in_progress">{isRTL ? 'جاري' : 'In Progress'}</SelectItem>
                      <SelectItem value="completed">{isRTL ? 'مكتمل' : 'Completed'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'ملاحظات' : 'Notes'}</Label>
                <Textarea 
                  value={stageData.implementation?.notes || ''} 
                  onChange={e => updateStageField('implementation', 'notes', e.target.value)}
                  rows={2}
                />
              </div>
              <div className={`flex justify-end pt-4 border-t ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Button onClick={handleSaveWorkflow}>
                  <Save className="h-4 w-4 mr-2" />
                  {isRTL ? 'حفظ التغييرات' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Site Complete Stage */}
        <TabsContent value="site_complete" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <CheckCircle className="h-5 w-5" /> {isRTL ? 'اكتمال الموقع' : 'Site Complete'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isRTL ? 'تاريخ الاكتمال' : 'Completion Date'}</Label>
                  <Input 
                    type="date"
                    value={stageData.site_complete?.completion_date || ''} 
                    onChange={e => updateStageField('site_complete', 'completion_date', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'اكتمل بواسطة' : 'Completed By'}</Label>
                  <Input 
                    value={stageData.site_complete?.completed_by || ''} 
                    onChange={e => updateStageField('site_complete', 'completed_by', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'الحالة' : 'Status'}</Label>
                  <Select 
                    value={stageData.site_complete?.status || 'not_started'} 
                    onValueChange={v => updateStageField('site_complete', 'status', v)}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">{isRTL ? 'لم يبدأ' : 'Not Started'}</SelectItem>
                      <SelectItem value="completed">{isRTL ? 'مكتمل' : 'Completed'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'ملاحظات' : 'Notes'}</Label>
                <Textarea 
                  value={stageData.site_complete?.notes || ''} 
                  onChange={e => updateStageField('site_complete', 'notes', e.target.value)}
                  rows={2}
                />
              </div>
              <div className={`flex justify-end pt-4 border-t ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Button onClick={handleSaveWorkflow}>
                  <Save className="h-4 w-4 mr-2" />
                  {isRTL ? 'حفظ التغييرات' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Handover Stage */}
        <TabsContent value="handover" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <FileText className="h-5 w-5" /> {isRTL ? 'التسليم' : 'Handover'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isRTL ? 'تاريخ التسليم' : 'Handover Date'}</Label>
                  <Input 
                    type="date"
                    value={stageData.handover?.handover_date || ''} 
                    onChange={e => updateStageField('handover', 'handover_date', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'سلم إلى' : 'Handed To'}</Label>
                  <Input 
                    value={stageData.handover?.handed_to || ''} 
                    onChange={e => updateStageField('handover', 'handed_to', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'توقيع بواسطة' : 'Sign-off By'}</Label>
                  <Input 
                    value={stageData.handover?.sign_off_by || ''} 
                    onChange={e => updateStageField('handover', 'sign_off_by', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'الحالة' : 'Status'}</Label>
                  <Select 
                    value={stageData.handover?.status || 'not_started'} 
                    onValueChange={v => updateStageField('handover', 'status', v)}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">{isRTL ? 'لم يبدأ' : 'Not Started'}</SelectItem>
                      <SelectItem value="handed_over">{isRTL ? 'تم التسليم' : 'Handed Over'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'ملاحظات' : 'Notes'}</Label>
                <Textarea 
                  value={stageData.handover?.notes || ''} 
                  onChange={e => updateStageField('handover', 'notes', e.target.value)}
                  rows={2}
                />
              </div>
              <div className={`flex justify-end pt-4 border-t ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Button onClick={handleSaveWorkflow}>
                  <Save className="h-4 w-4 mr-2" />
                  {isRTL ? 'حفظ التغييرات' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PAT Document Stage */}
        <TabsContent value="pat_document" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <FileText className="h-5 w-5" /> {isRTL ? 'وثيقة PAT' : 'PAT Document'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isRTL ? 'تاريخ PAT' : 'PAT Date'}</Label>
                  <Input 
                    type="date"
                    value={stageData.pat_document?.pat_date || ''} 
                    onChange={e => updateStageField('pat_document', 'pat_date', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'رقم المرجع' : 'Document Reference'}</Label>
                  <Input 
                    value={stageData.pat_document?.document_ref || ''} 
                    onChange={e => updateStageField('pat_document', 'document_ref', e.target.value)}
                    placeholder="PAT-001"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'موقع بواسطة' : 'Signed By'}</Label>
                  <Input 
                    value={stageData.pat_document?.signed_by || ''} 
                    onChange={e => updateStageField('pat_document', 'signed_by', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'الحالة' : 'Status'}</Label>
                  <Select 
                    value={stageData.pat_document?.status || 'not_started'} 
                    onValueChange={v => updateStageField('pat_document', 'status', v)}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">{isRTL ? 'لم يبدأ' : 'Not Started'}</SelectItem>
                      <SelectItem value="signed">{isRTL ? 'موقع' : 'Signed'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'ملاحظات' : 'Notes'}</Label>
                <Textarea 
                  value={stageData.pat_document?.notes || ''} 
                  onChange={e => updateStageField('pat_document', 'notes', e.target.value)}
                  rows={2}
                />
              </div>
              <div className={`flex justify-end pt-4 border-t ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Button onClick={handleSaveWorkflow}>
                  <Save className="h-4 w-4 mr-2" />
                  {isRTL ? 'حفظ التغييرات' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Troubleshoot Stage */}
        <TabsContent value="troubleshoot" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <AlertCircle className="h-5 w-5" /> {isRTL ? 'استكشاف الأخطاء' : 'Troubleshoot'} 
                <Badge className="ml-2">{isRTL ? 'اختياري' : 'Optional'}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isRTL ? 'تاريخ المشكلة' : 'Issue Date'}</Label>
                  <Input 
                    type="date"
                    value={stageData.troubleshoot?.issue_date || ''} 
                    onChange={e => updateStageField('troubleshoot', 'issue_date', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'نوع المشكلة' : 'Issue Type'}</Label>
                  <Input 
                    value={stageData.troubleshoot?.issue_type || ''} 
                    onChange={e => updateStageField('troubleshoot', 'issue_type', e.target.value)}
                    placeholder={isRTL ? 'مثال: عطل في الاتصال' : 'e.g., Connectivity Issue'}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>{isRTL ? 'وصف المشكلة' : 'Issue Description'}</Label>
                  <Textarea 
                    value={stageData.troubleshoot?.issue_description || ''} 
                    onChange={e => updateStageField('troubleshoot', 'issue_description', e.target.value)}
                    rows={2}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>{isRTL ? 'الحل' : 'Resolution'}</Label>
                  <Textarea 
                    value={stageData.troubleshoot?.resolution || ''} 
                    onChange={e => updateStageField('troubleshoot', 'resolution', e.target.value)}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'تاريخ الحل' : 'Resolved Date'}</Label>
                  <Input 
                    type="date"
                    value={stageData.troubleshoot?.resolved_date || ''} 
                    onChange={e => updateStageField('troubleshoot', 'resolved_date', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'الحالة' : 'Status'}</Label>
                  <Select 
                    value={stageData.troubleshoot?.status || 'none'} 
                    onValueChange={v => updateStageField('troubleshoot', 'status', v)}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{isRTL ? 'لا يوجد مشاكل' : 'No Issues'}</SelectItem>
                      <SelectItem value="open">{isRTL ? 'مفتوح' : 'Open'}</SelectItem>
                      <SelectItem value="resolved">{isRTL ? 'تم الحل' : 'Resolved'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className={`flex justify-end pt-4 border-t ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Button onClick={handleSaveWorkflow}>
                  <Save className="h-4 w-4 mr-2" />
                  {isRTL ? 'حفظ التغييرات' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Stage */}
        <TabsContent value="invoices" className="mt-4">
          <Card>
            <CardHeader className={`flex flex-row items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Receipt className="h-5 w-5" /> {isRTL ? 'الفواتير' : 'Invoices'}
              </CardTitle>
              <Button size="sm" onClick={() => { setInvoiceForm({}); setShowInvoiceDialog(true); }}>
                <Plus className="h-4 w-4 mr-1" /> {isRTL ? 'إضافة فاتورة' : 'Add Invoice'}
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(stageData.invoices || []).map((invoice, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-3 bg-muted/50 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <Receipt className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium">{invoice.invoice_number}</p>
                        <p className="text-sm text-muted-foreground">{invoice.invoice_type === 'partial' ? (isRTL ? 'جزئية' : 'Partial') : (isRTL ? 'كاملة' : 'Full')}</p>
                      </div>
                    </div>
                    <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span className="font-semibold">{currencySymbol} {(invoice.amount || 0).toLocaleString()}</span>
                      {getStatusBadge(invoice.status)}
                      <span className="text-sm text-muted-foreground">{invoice.invoice_date}</span>
                    </div>
                  </div>
                ))}
                {(!stageData.invoices || stageData.invoices.length === 0) && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Receipt className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>{isRTL ? 'لا توجد فواتير بعد' : 'No invoices yet'}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Team Dialog */}
      <Dialog open={showTeamDialog} onOpenChange={setShowTeamDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isRTL ? 'إضافة عضو فريق' : 'Add Team Member'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isRTL ? 'الموظف' : 'Employee'} *</Label>
              <Select value={teamForm.employee_id || ''} onValueChange={v => setTeamForm({...teamForm, employee_id: v})}>
                <SelectTrigger><SelectValue placeholder={isRTL ? 'اختر الموظف' : 'Select Employee'} /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'الدور' : 'Role'}</Label>
              <Input value={teamForm.role || ''} onChange={e => setTeamForm({...teamForm, role: e.target.value})} placeholder={isRTL ? 'مثال: فني' : 'e.g., Technician'} />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'تاريخ التعيين' : 'Assigned Date'}</Label>
              <Input type="date" value={teamForm.assigned_date || ''} onChange={e => setTeamForm({...teamForm, assigned_date: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'ملاحظات' : 'Notes'}</Label>
              <Textarea value={teamForm.notes || ''} onChange={e => setTeamForm({...teamForm, notes: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTeamDialog(false)}>{isRTL ? 'إلغاء' : 'Cancel'}</Button>
            <Button onClick={handleAddTeam}>{isRTL ? 'إضافة' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isRTL ? 'إضافة فاتورة' : 'Add Invoice'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isRTL ? 'رقم الفاتورة' : 'Invoice Number'}</Label>
                <Input value={invoiceForm.invoice_number || ''} onChange={e => setInvoiceForm({...invoiceForm, invoice_number: e.target.value})} placeholder="INV-001" />
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'تاريخ الفاتورة' : 'Invoice Date'}</Label>
                <Input type="date" value={invoiceForm.invoice_date || ''} onChange={e => setInvoiceForm({...invoiceForm, invoice_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'المبلغ' : 'Amount'} *</Label>
                <Input type="number" value={invoiceForm.amount || ''} onChange={e => setInvoiceForm({...invoiceForm, amount: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'النوع' : 'Type'}</Label>
                <Select value={invoiceForm.invoice_type || 'partial'} onValueChange={v => setInvoiceForm({...invoiceForm, invoice_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="partial">{isRTL ? 'جزئية' : 'Partial'}</SelectItem>
                    <SelectItem value="full">{isRTL ? 'كاملة' : 'Full'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>{isRTL ? 'الحالة' : 'Status'}</Label>
                <Select value={invoiceForm.status || 'not_started'} onValueChange={v => setInvoiceForm({...invoiceForm, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">{isRTL ? 'لم يبدأ' : 'Not Started'}</SelectItem>
                    <SelectItem value="sent">{isRTL ? 'مرسلة' : 'Sent'}</SelectItem>
                    <SelectItem value="paid">{isRTL ? 'مدفوعة' : 'Paid'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? 'ملاحظات' : 'Notes'}</Label>
              <Textarea value={invoiceForm.notes || ''} onChange={e => setInvoiceForm({...invoiceForm, notes: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>{isRTL ? 'إلغاء' : 'Cancel'}</Button>
            <Button onClick={handleAddInvoice}>{isRTL ? 'إضافة' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SiteDetail;
